import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { LoadingSpinner } from '../../../components/LoadingSpinner';
import {
  ConfigQueryKeys,
  getAlertPreferences,
  setAlertOptIn,
  updateAlertRecipients,
  type AlertOptInResponse,
  type TenantAlertPreferencesResponse,
} from '../api';

const ALERT_TYPE_LABEL: Record<string, string> = {
  NearUsageLimit: 'Cerca del límite de uso',
  WeeklyUsageSummary: 'Resumen semanal',
};

function alertTypeLabel(type: string): string {
  return ALERT_TYPE_LABEL[type] ?? type;
}

/**
 * Tenant "Alertas" tab: manage the email addresses that receive alert mail and the per-alert-type
 * opt-ins. Recipients are sent as a full desired set (the backend reconciles); opt-ins toggle one
 * alert type at a time. Both surfaces refresh the cached preferences on success.
 */
export function AlertsSection(): JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ConfigQueryKeys.alerts,
    queryFn: ({ signal }) => getAlertPreferences(signal),
  });

  const [newAddress, setNewAddress] = useState('');
  const [error, setError] = useState<string | null>(null);

  const onSuccess = (updated: TenantAlertPreferencesResponse): void => {
    queryClient.setQueryData(ConfigQueryKeys.alerts, updated);
    setError(null);
  };
  const onError = (e: unknown): void => {
    setError(e instanceof Error ? e.message : 'No se pudo guardar el cambio.');
  };

  const recipientsMutation = useMutation({
    mutationFn: updateAlertRecipients,
    onSuccess,
    onError,
  });
  const optInMutation = useMutation({
    mutationFn: setAlertOptIn,
    onSuccess,
    onError,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (isError || data === undefined) {
    return (
      <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
        No se pudieron cargar las preferencias de alertas.
      </div>
    );
  }

  const activeAddresses = data.recipients.filter((r) => r.state === 'Active').map((r) => r.address);
  const pending = recipientsMutation.isPending || optInMutation.isPending;

  const addRecipient = (): void => {
    const address = newAddress.trim();
    if (address === '') {
      return;
    }
    const next = Array.from(new Set([...activeAddresses, address]));
    recipientsMutation.mutate({ recipients: next.map((a) => ({ address: a })) });
    setNewAddress('');
  };

  const removeRecipient = (address: string): void => {
    const next = activeAddresses.filter((a) => a !== address);
    recipientsMutation.mutate({ recipients: next.map((a) => ({ address: a })) });
  };

  const toggleOptIn = (optIn: AlertOptInResponse): void => {
    optInMutation.mutate({ alertType: optIn.alertType, optedIn: !optIn.optedIn });
  };

  return (
    <div className="space-y-6" data-testid="alerts-section">
      <div>
        <h2 className="text-base font-semibold text-slate-900">Destinatarios de alertas</h2>
        <p className="mt-1 text-sm text-slate-500">
          Direcciones que reciben los correos de alerta (máximo 10).
        </p>

        <ul className="mt-3 space-y-2" data-testid="recipients-list">
          {activeAddresses.length === 0 ? (
            <li className="text-sm text-slate-400">Aún no hay destinatarios configurados.</li>
          ) : (
            activeAddresses.map((address) => (
              <li
                key={address}
                className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2"
              >
                <span className="text-sm text-slate-700">{address}</span>
                <button
                  type="button"
                  onClick={() => removeRecipient(address)}
                  disabled={pending}
                  className="text-xs font-medium text-rose-600 hover:underline disabled:opacity-50"
                  data-testid="remove-recipient"
                >
                  Quitar
                </button>
              </li>
            ))
          )}
        </ul>

        <div className="mt-3 flex gap-2">
          <input
            type="email"
            value={newAddress}
            onChange={(e) => setNewAddress(e.target.value)}
            placeholder="ops@empresa.com"
            aria-label="Nueva dirección de alerta"
            className="input max-w-xs"
            data-testid="new-recipient-input"
          />
          <button
            type="button"
            onClick={addRecipient}
            disabled={pending || newAddress.trim() === ''}
            className="btn-primary"
            data-testid="add-recipient"
          >
            Añadir
          </button>
        </div>
      </div>

      <div>
        <h2 className="text-base font-semibold text-slate-900">Tipos de alerta</h2>
        <p className="mt-1 text-sm text-slate-500">Activa o desactiva cada tipo de alerta para tu organización.</p>

        <ul className="mt-3 space-y-2">
          {data.optIns.map((optIn) => (
            <li
              key={optIn.alertType}
              className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2"
            >
              <span className="text-sm text-slate-700">{alertTypeLabel(optIn.alertType)}</span>
              <button
                type="button"
                onClick={() => toggleOptIn(optIn)}
                disabled={pending}
                aria-pressed={optIn.optedIn}
                className={
                  'rounded-full px-3 py-1 text-xs font-medium disabled:opacity-50 ' +
                  (optIn.optedIn ? 'bg-primary text-white' : 'border border-slate-300 text-slate-600')
                }
                data-testid={`opt-in-${optIn.alertType}`}
              >
                {optIn.optedIn ? 'Activada' : 'Desactivada'}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {error !== null ? <p className="text-xs text-rose-700">{error}</p> : null}
    </div>
  );
}
